/**
 * Os textos fixos do Termo de Recebimento Provisório.
 *
 * **Ficam aqui, e não no glossário, pela regra do próprio glossário:** ele existe para o texto que
 * aparece em mais de uma tela — é ele que evita "Enviar ao comitê" numa tela e "Submeter RDM"
 * noutra. Estes só existem neste documento. O que é compartilhado (sigla, ajuda do TRP, rótulos dos
 * indicadores) continua vindo de `glossario.ts`.
 *
 * **A redação é a do TRP nº 01/SSTD/RGG/2026**, o documento assinado em 11/08/2026, transcrita.
 * Reescrevê-la em linguagem própria faria o termo novo e a pilha já arquivada parecerem dois
 * documentos diferentes — e é a fiscalização que assina os dois.
 */
export const TRP = {
  titulo: 'Termo de Recebimento Provisório da Fiscalização Técnica de Sustentação',
  contrato: 'Contrato SEXEC/SSTD nº 01/2026',
  objeto:
    'Execução de serviços especializados para versão customizada do software público de gestão municipal ' +
    'e-Cidade (software livre sob licença GPL), incluindo também sistemas complementares integrados com o ' +
    'software público e-Cidade, conforme as condições estabelecidas no Termo de Referência e seus anexos.',
  contratante: 'MUNICÍPIO DE NITERÓI',
  cnpjContratante: '28.521.748/0001-59',
  contratada: 'DBSELLER SERVIÇOS DE INFORMÁTICA LTDA.',
  cnpjContratada: '05.238.851/0001-90',
  processo: '9900046819/2023',

  secoes: {
    introducao: 'I. Introdução',
    analise: 'II. Análise Quantitativa e Qualitativa',
    niveis: 'III. Mensuração dos Níveis Mínimos de Serviço',
    ocorrencias: 'IV. Registro de Ocorrências',
    conclusao: 'V. Conclusão',
  },

  introducao: (periodo: string) =>
    `Este termo detalhado tem por objetivo descrever as análises da fiscalização técnica relativas à ` +
    `execução do contrato em epígrafe, para o período de ${periodo}, a fim de atestar o recebimento ` +
    'provisório, registrar os bens ou serviços recusados e os níveis mínimos de serviço mensurados, na ' +
    'forma do art. 21, X e XII do Decreto Municipal nº 14.730, de 2023.',

  etapas:
    'A mensuração da quantidade e qualidade foi realizada na forma descrita no modelo de execução e no ' +
    'modelo de gestão, ambos do termo de referência, itens 6, 7 e respectivos subitens, e no plano de ' +
    'fiscalização elaborado pela equipe. Para esta finalidade, foram realizadas as seguintes etapas: ' +
    'a) Recebimento pelo Gestor do Contrato da planilha dos chamados atendidos e pendentes no e-Niterói no ' +
    'mês de referência, o Relatório de Faturamento e a descrição completa de todo o atendimento no ' +
    'e-Niterói; b) Carga da planilha no Sistema de Gestão; c) Análise pelo Fiscal Técnico dos chamados ' +
    'atendidos e pendentes, bem como dos níveis mínimos de serviço; d) Elaboração do Termo de Recebimento ' +
    'Provisório.',

  fundamentoDosIndicadores:
    'Os indicadores dos níveis mínimos de serviços encontram-se previstos no item 4.1.2.3.14 e ANEXO VIII ' +
    '– ACORDO DE NÍVEL DE SERVIÇO do termo de referência. O quadro a seguir apresenta a síntese das ' +
    'informações apuradas:',

  instrumentos:
    'Para a mensuração dos níveis, os chamados do período foram carregados no Sistema de Gestão e julgados ' +
    'um a um pela fiscalização técnica. O cálculo de cada indicador, os denominadores e a composição de ' +
    'cada numerador estão registrados no sistema e podem ser conferidos chamado a chamado.',

  quadroApuracao: {
    indicador: 'Nome do Indicador',
    calculo: 'Cálculo Aplicado',
    nivel: 'Nível apurado',
    faixa: 'Faixa Alcançada',
    evidencia: 'Evidência',
    evidenciaPadrao: 'O cálculo realizado encontra-se registrado no Sistema de Gestão.',
  },

  conclusao:
    'Por todo exposto, a fiscalização técnica atesta, para finalidade de recebimento provisório, que o ' +
    'fornecimento de bens e serviços foi executado no período avaliado, observados os níveis mínimos de ' +
    'serviço apurados acima e a redução pecuniária deles decorrente.',

  semOcorrencias:
    'A fiscalização técnica não registrou ocorrências administrativas neste período. A execução contratual ' +
    'ocorreu, quanto a este aspecto, em conformidade com o termo de referência e o contrato.',

  comOcorrencias: (quantas: number, pontos: number) =>
    `A fiscalização técnica registrou ${quantas} ocorrência(s) administrativa(s) no período, somando ` +
    `${pontos} ponto(s) na forma do ANEXO VIII, item iv (pp. 157-159). O detalhamento de cada uma, com a ` +
    'data do fato e a manifestação da contratada quando houve, está registrado no Sistema de Gestão.',

  somaDeRedutores: 'Soma dos redutores por incidente (ANEXO VIII, alínea n, p. 161)',
  somaDoTis: (soma: number, teto: number) =>
    soma > teto
      ? `${soma}% somados, limitados ao teto de ${teto}% do indicador`
      : `${soma}% somados (teto do indicador: ${teto}%)`,

  assinatura: {
    fiscal: 'Fiscal Técnico',
    aviso:
      'Nome, matrícula e portaria de designação do fiscal técnico não são gerados pelo sistema: quem assina ' +
      'o termo preenche à mão ou por carimbo.',
  },
} as const

/**
 * O que este documento NÃO traz, e por quê.
 *
 * **Não é "campo em branco".** É a mesma disciplina do indicador não apurado: quando o sistema não
 * tem o dado, ele diz que não tem e diz por quê, em vez de imprimir um zero que se lê como
 * afirmação. Numa folha que vai para processo administrativo isso vale ainda mais — a folha circula
 * solta, e quem a receber não terá esta tela ao lado para perguntar.
 */
export const RESSALVA = {
  titulo: 'O que este termo não traz',
  semReais:
    'Este termo é emitido SEM os valores em reais. O valor faturado da competência não está registrado ' +
    'neste sistema — não é lacuna de programação, é um dado que depende de ato de gestão.',
  oQueOSistemaTem:
    'O que foi apurado é o PERCENTUAL de redução, e ele está impresso na seção V: a soma dos redutores, o ' +
    'teto de 10% do item h (p. 148) e o percentual que efetivamente incide.',
  /**
   * Era `aDecisaoQueFalta`, e imprimia que "falta decidir se a glosa incide sobre um valor único ou
   * por linha de serviço". **Foi decidido em 26/08/2026** — valor único —, e uma folha que vai para
   * processo administrativo dizendo que a base do cálculo está indefinida é munição de graça numa
   * impugnação.
   *
   * O que entra no lugar não é a decisão sozinha: é a decisão com a leitura contrária ao lado e o
   * porquê de a escolhida ser a mais rigorosa. Um termo que só afirma convence menos que um termo
   * que mostra ter visto as duas leituras.
   */
  aRessalvaDeLeitura:
    'A glosa incide sobre um valor único do mês, com um teto de 10% sobre a soma dos indicadores. ' +
    'É a leitura da alínea h do ANEXO VIII (p. 148), que manda somar os percentuais no final do ' +
    'período de faturamento, e do item 8.10 (p. 56), segundo o qual os descontos se acumulam na ' +
    'mesma ordem de serviço. A leitura contrária — um teto por linha de serviço — aparece na ' +
    'fórmula do ANEXO V (p. 132) e ainda não teve incidência prática neste contrato. O teto único é ' +
    'sempre igual ou mais rigoroso com a contratada do que o teto por linha.',
  oQueFazer:
    'As células em reais deste termo devem ser preenchidas pela fiscalização sobre a nota fiscal do mês, ' +
    'aplicando o percentual acima. Este sistema não as calcula e não as confere.',
  semNumeroNaoEhZero: 'Sem número não é zero. Zero é o resultado de quem cumpriu a meta.',
  outrosAusentes: [
    'Número do Processo de Pagamento e número sequencial do TRP — atribuídos fora deste sistema.',
    'Quantidade proporcional de dias do mês (a fração 25/30 do termo de maio/2026) — depende do valor.',
    'Nome, matrícula e portaria de designação do fiscal técnico.',
  ],
} as const
